import { ShoppingOutlined } from '@ant-design/icons'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Button, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { FC } from 'react'
import { SortableDragHandle, SortableRow } from '../../../../../../../../components/sortable-row'
import renderDatetime from '../../../../../../../../helpers/tables/render-datetime'
import Form from '../../../../../../../../types/api/form'
import { DocumentId } from '../../../../../../../../types/strapi/strapi'
import ColorSwatch from '../../../color-swatch'
import LastOrderCell from '../../../last-order-cell'
import { RemoveFormFromSet } from './remove-form-from-set'

interface FormsTableProps {
    formSetDocId: DocumentId
    forms: Form[]
    draftIds?: string[]
    orderSaved?: boolean
    hideOrderActions?: boolean
    onAddToDraft?: (formDocumentId: string) => void
    onReorderForms?: (activeId: string, overId: string) => void | Promise<void>
    onAddAllToDraft?: () => void
}

export const FormsTable: FC<FormsTableProps> = ({
    formSetDocId,
    forms,
    draftIds = [],
    orderSaved,
    hideOrderActions,
    onAddToDraft,
    onReorderForms,
    onAddAllToDraft,
}) => {
    const canDrag = !orderSaved && !!onReorderForms

    const columns: ColumnsType<Form> = []

    if (canDrag) {
        columns.push({
            key: 'sort',
            width: 40,
            render: () => <SortableDragHandle />,
        })
    }

    columns.push(
        { title: 'Номер формы', dataIndex: 'num', key: 'num', width: 120 },
        {
            title: 'Цвет',
            dataIndex: 'color',
            key: 'color',
            width: 80,
            render: (_, r) => (r.color ? <ColorSwatch $hex={r.color} /> : null),
        },
        { title: 'Название', dataIndex: 'colorName', key: 'colorName' },
        {
            title: 'Полимер',
            dataIndex: 'polymer',
            key: 'polymer',
            width: 140,
            render: (_, r) => r.polymer || null,
        },
        {
            title: 'Последний заказ',
            key: 'form_order',
            width: 160,
            render: (_, r) => (
                <LastOrderCell
                    status={r.form_order?.orderStatus}
                    estimatedDate={r.form_order?.estimatedDate}
                />
            ),
        },
        {
            title: 'Детали',
            key: 'failure',
            width: 180,
            render: (_, r) => {
                if (!r.failureReason && !r.dateOfFailure) return null

                return (
                    <>
                        {r.failureReason}
                        {r.failureReason && r.dateOfFailure ? <br /> : null}
                        {r.dateOfFailure ? renderDatetime(r.dateOfFailure) : null}
                    </>
                )
            },
        }
    )

    if (hideOrderActions) {
        columns.push({
            title: 'Действия',
            key: 'actions',
            width: 80,
            render: (_, r) => (
                <Flex justify='end' style={{ width: '100%' }}>
                    <RemoveFormFromSet formDocId={r.documentId} formSetDocId={formSetDocId} />
                </Flex>
            ),
        })
    } else {
        columns.push({
            title: (
                <Flex justify='end'>
                    <Button
                        type='primary'
                        icon={<ShoppingOutlined />}
                        disabled={
                            orderSaved ||
                            forms.length === 0 ||
                            forms.every(f => draftIds.includes(f.documentId))
                        }
                        onClick={onAddAllToDraft}
                    >
                        Все в заказ
                    </Button>
                </Flex>
            ),
            key: 'actions',
            width: 200,
            render: (_, r) => {
                const inDraft = draftIds.includes(r.documentId)

                return (
                    <Flex justify='end' gap={4} style={{ width: '100%' }}>
                        <Button
                            type={'primary'}
                            icon={<ShoppingOutlined />}
                            disabled={orderSaved || inDraft}
                            onClick={() => onAddToDraft?.(r.documentId)}
                        >
                            {inDraft ? 'В черновике' : 'В заказ'}
                        </Button>
                        <RemoveFormFromSet formDocId={r.documentId} formSetDocId={formSetDocId} />
                    </Flex>
                )
            },
        })
    }

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id || !onReorderForms) return
        onReorderForms(String(active.id), String(over.id))
    }

    const table = (
        <Table
            rowKey='documentId'
            columns={columns}
            dataSource={forms}
            pagination={false}
            style={{ width: '100%', minHeight: 200, marginTop: 12 }}
            components={canDrag ? { body: { row: SortableRow } } : undefined}
        />
    )

    if (!canDrag) return table

    return (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
            <SortableContext
                items={forms.map(form => form.documentId)}
                strategy={verticalListSortingStrategy}
            >
                {table}
            </SortableContext>
        </DndContext>
    )
}
